<template>
  <v-form
    ref="form"
    v-model="valid"
    lazy-validation
    style="width: 100%; max-width: 890px"
    @submit.prevent="validateForm"
  >
    <div class="pa-8 fill-height" style="width: 100%">
      <v-row>
        <v-col>
          <template v-for="item in allItems">
            <DuaMessageTripCard
              :item="item"
              :key="item.uuid"
              :type="item._type"
              :item-uuid="item.uuid"
              :ref-form="$refs.form"
              :validating-form="validatingForm"
            />
          </template>
        </v-col>
      </v-row>
      <transition name="slide-y-transition" mode="out-in">
        <v-row v-if="diffItemsCount && !isHistoryEvent">
          <v-col class="d-flex justify-end">
            <v-btn
              type="submit"
              rounded
              color="primary"
              :disabled="disabledPublishButton"
              :loading="sendingMessage"
            >
              <v-icon small class="pr-4">mdi-send</v-icon>
              Publicera
            </v-btn>
          </v-col>
        </v-row>
      </transition>
      <v-row v-if="event.last_dua_message && !isHistoryEvent">
        <v-col class="d-flex justify-end">
          <span
            ><span class="text-subtitle-2 text--secondary font-weight-bold"
              >Senast skickat:</span
            >
            {{ event.last_dua_message.created | dateTime }}</span
          >
        </v-col>
      </v-row>
    </div>
  </v-form>
</template>

<script>
import { get } from 'vuex-pathify'
import diffFactory from '@/helpers/diffFactory'
import DuaMessageTripCard from './DuaMessageTripCard'
import gql from 'graphql-tag'
import { format } from 'date-fns'
import orderBy from 'lodash.orderby'

export default {
  name: 'DuaMessageForm',

  components: {
    DuaMessageTripCard
  },

  filters: {
    dateTime(date) {
      return format(new Date(date), 'yyyy-MM-dd HH:mm')
    }
  },

  data: () => ({
    validatingForm: false,
    valid: true,
    sendingMessage: false
  }),

  computed: {
    trainDeviations: get('trainDeviation/data'),
    event: get('event/selectedEvent'),
    model: get('duaMessage/model'),
    form: get('duaMessage/form'),
    selectedEventUUID: get('event/selectedUUID'),
    diffItemsCount: get('duaMessage/diffItemsCount'),
    isHistoryEvent: get('history/isHistoryEvent'),
    diffTrainDeviations: get('duaMessage/diffTrainDeviations'),
    diffTrainDeviationsCancelSections: get(
      'duaMessage/diffTrainDeviationsCancelSections'
    ),

    list() {
      return diffFactory(
        this.model?.body?.map(x => x?.trip_uuid)?.filter(Boolean) || [],
        x => x,
        this.trainDeviations
      ).filter(x => x.text.deviation_type !== 'ADDED')
    },

    currentItems() {
      return this.list
        .filter(x => x.current)
        .map(x => ({
          ...x,
          _type: 'current'
        }))
    },

    newItems() {
      return this.list
        .filter(x => x.new && this.$route.name !== 'HistoryEvent')
        .map(x => ({
          ...x,
          _type: 'new'
        }))
    },

    missingItems() {
      return this.list
        .filter(x => x.missing && this.$route.name !== 'HistoryEvent')
        .map(x => ({
          ...x,
          _type: 'missing'
        }))
    },

    allItems() {
      return orderBy(
        [...this.currentItems, ...this.missingItems, ...this.newItems],
        [x => x.text.departure],
        ['asc']
      )
    },

    disabledPublishButton() {
      const items = [
        ...this.newItems,
        ...this.currentItems.filter(x => this.isDiff(x.uuid))
      ]

      return (
        !this.missingItems.length &&
        !items.map(x => this.form[x.uuid]).some(x => x._checked)
      )
    }
  },
  methods: {
    isDiff(uuid) {
      const isOldDiff =
        ['CANCELED_STOP'].includes(this.trainDeviations[uuid].deviation_type) ||
        !this.form[uuid]?.data?.cancel_sections.length
      if (isOldDiff) {
        return Boolean(
          this.diffTrainDeviations?.find(x => x?.trip_uuid === uuid)
        )
      }

      return this.diffTrainDeviationsCancelSections?.find(
        x => x?.trip_uuid === uuid
      )
    },

    validateForm() {
      this.validatingForm = true

      this.$nextTick(this.sendDuaMessage)
    },

    async sendDuaMessage() {
      const isValidForm = await this.$refs.form.validate()

      if (!isValidForm) {
        this.validatingForm = false
        return
      }

      this.sendingMessage = true

      const mapCurrentUUIDs = array =>
        array
          .filter(x => x.current)
          .map(x => this.form[x.uuid])
          .map(x => {
            if (!x._checked) {
              return {
                ...x,
                data:
                  this.model?.body?.find(y => y?.trip_uuid === x?.trip_uuid)
                    ?.data || null
              }
            }
            return x
          })
          .map(x => {
            delete x._checked
            return x
          })

      const mapNewUUIDs = array =>
        array
          .filter(x => x.new)
          .map(x => this.form[x.uuid])
          .filter(x => x._checked)
          .map(x => {
            delete x._checked
            return x
          })

      const setOriginalDataProps = item => {
        if (
          item.data?.cancel_sections?.length &&
          !['CANCELED_STOP'].includes(
            this.trainDeviations[item.trip_uuid].deviation_type
          )
        ) {
          return {
            ...item,
            data: {
              ...item.data,
              dua_cancellation_code:
                item.data.cancel_sections[0].dua_cancellation_code,
              dua_reference_code:
                item.data.cancel_sections[0].dua_reference_code,
              estimated_time_at_location:
                item.data.cancel_sections[0].estimated_time_at_location,
              replacement_from_place: item.data.cancel_sections[0].from,
              replacement_to_place: item.data.cancel_sections[0].to
            }
          }
        }

        return item
      }

      const payload = {
        event_uuid: this.selectedEventUUID,
        trips: [
          ...mapCurrentUUIDs(this.list).map(setOriginalDataProps),
          ...mapNewUUIDs(this.list).map(setOriginalDataProps)
        ]
      }

      const sendPayload = {
        event_uuid: payload.event_uuid,
        trips: payload.trips.map(item => {
          const newPayload = Object.assign({}, item)
          if (
            !['CANCELED', 'PARTIAL_CANCELLATION'].includes(
              this.trainDeviations[newPayload.trip_uuid].deviation_type
            )
          ) {
            delete newPayload.data.cancel_sections
          }

          return newPayload
        })
      }

      await this.$apollo.mutate({
        mutation: gql`
          mutation($payload: SendDuaMessageInput!) {
            sendDuaMessage(payload: $payload) {
              dua_uuid
            }
          }
        `,
        variables: {
          payload: sendPayload
        }
      })

      this.$store.set(
        `event/data@${this.selectedEventUUID}.last_dua_message.created`,
        new Date().toString()
      )

      this.$store.set('duaMessage/model', {
        created: new Date().toString(),
        body: payload.trips
      })

      this.$store.dispatch('duaMessage/setForm')

      this.$eventBus.$emit('event/refetch')
      this.validatingForm = false
      this.sendingMessage = false
    }
  }
}
</script>
